/**
 * Answer-first for questions the site already answers (Muse audit 2026-10-20,
 * C1). "What areas do you cover?" got a lead-qualification reply although the
 * Contact FAQ publishes the answer. Pure: no DB, no model call. The answers are
 * the Contact FAQ items word for word, so Sarah never says more than the page.
 */
import { CONTACT_FAQ_ITEMS } from '@/app/contact/_v3/contact-constants'

export type SarahSiteFactId = 'areas' | 'showing' | 'response'

export type SarahSiteFact = {
  id: SarahSiteFactId
  question: string
  answer: string
  /** Public page that publishes the answer. */
  source: string
  match: RegExp
}

const CONTACT_SOURCE = '/contact'

function faq(index: number): { question: string; answer: string } {
  const item = CONTACT_FAQ_ITEMS[index]
  return { question: item.question, answer: item.answer }
}

export const SARAH_SITE_FACTS: readonly SarahSiteFact[] = [
  {
    id: 'areas',
    ...faq(0),
    source: CONTACT_SOURCE,
    match:
      /\b(?:what|which)\s+(?:areas?|cities|towns|places|regions?|parts of (?:central )?oregon)\b[^?]*\b(?:cover|serve|service|work|help|sell|list)|\bservice areas?\b|\b(?:areas?|cities|towns) (?:do )?you (?:cover|serve|work)|\bwhere do you (?:cover|serve|work|operate|sell)\b|\bdo you (?:cover|serve|work in) (?:all of )?central oregon\b/i,
  },
  {
    id: 'showing',
    ...faq(1),
    source: CONTACT_SOURCE,
    match: /\bhow (?:do|can) (?:i|we) (?:schedule|book|set up|arrange|request) (?:a |an )?(?:showing|tour|viewing)\b/i,
  },
  {
    id: 'response',
    ...faq(2),
    source: CONTACT_SOURCE,
    match:
      /\bhow (?:quickly|fast|soon|long)\b[^?]*\b(?:hear back|respond|reply|get back|call (?:me )?back)\b|\bresponse time\b/i,
  },
]

export type SarahSiteAnswer = {
  id: SarahSiteFactId
  reply: string
  source: string
}

/**
 * The Contact FAQ says "the form on this page". Off /contact that would point
 * at the wrong page, so only that phrase is pointed at the Contact page.
 */
function placeAnswer(answer: string, pathname: string | null | undefined): string {
  const onContact = (pathname ?? '').replace(/\/+$/, '') === CONTACT_SOURCE
  if (onContact) return answer
  return answer.replace(/\bthe form on this page\b/gi, 'the form on our Contact page')
}

export function matchSiteFact(text: string): SarahSiteFact | null {
  const q = text.trim()
  if (!q) return null
  return SARAH_SITE_FACTS.find((fact) => fact.match.test(q)) ?? null
}

/** A published answer for a factual question, or null when the site does not answer it. */
export function answerSiteQuestion(
  text: string,
  opts: { pathname?: string | null } = {},
): SarahSiteAnswer | null {
  const fact = matchSiteFact(text)
  if (!fact) return null
  return { id: fact.id, reply: placeAnswer(fact.answer, opts.pathname), source: fact.source }
}

/** The same facts as a prompt block, so voice answers from them too. */
export function siteFactsPromptBlock(): string {
  const rows = SARAH_SITE_FACTS.map(
    (fact) => `- Q: "${fact.question}" A (published on the ${fact.source} page): "${fact.answer}"`,
  )
  return [
    'SITE FACTS: These are published on our site. When someone asks a factual question these answer, answer it from them first, in their words. Do not answer a factual question with a qualifying question. After the answer, at most one soft follow-up.',
    ...rows,
  ].join('\n')
}
